//TODO: relocate block specific logic (ex: pipe/prison)
import { captureGenericError } from 'src/lib/sentry';
import type { Entity, QueriesObject, QueryResults, System } from '../../odie';

import { BlockComponent } from '../components/BlockComponent';
import { GoalId } from '../defs/goal';
import type { MapDef } from '../defs/map';
import { BlockEntity } from '../entities/BlockEntity';
import {
  createSessionEntity,
  despawnSessionEntity,
  spawnSessionEntity,
} from '../entities/SessionEntity';
import type { GameScene } from '../GameScene';
import { PuzzleMapImporter } from '../util/PuzzleMapImporter';
import { getSaveStateData } from '../../savestate/match2-savestate.data';
import StateObserver from 'src/StateObserver';

/*
    game session control
*/
export class SessionSystem implements System {
  // constants
  //-------------------------------------------------------------------------
  public static readonly NAME = 'session';
  public static Queries: QueriesObject = {
    block: {
      components: [BlockComponent],
      removed: true,
    },
  };

  // fields
  //-------------------------------------------------------------------------
  // injected
  public scene!: GameScene;
  public queries!: QueryResults;
  // input
  private _mapDef?: MapDef;

  // impl
  //-------------------------------------------------------------------------
  public init() {
    //
  }

  public awake() {
    if (!this._mapDef) throw new Error('map not set');

    // create session entity
    const sessionEntity = (this.scene.sessionEntity = createSessionEntity(
      this._mapDef,
    ));

    // spawn session entity
    spawnSessionEntity(this.scene, sessionEntity);
  }

  public reset() {
    // despawn session entity
    despawnSessionEntity(this.scene, this.scene.sessionEntity);
  }

  public removedFromQuery(entity: Entity) {
    // call despawn handler
    this._onBlockDespawn(entity as BlockEntity);
  }

  // api
  //-------------------------------------------------------------------------
  public setMap(mapDef: MapDef) {
    // create session entity with given map
    this._mapDef = mapDef;
  }

  public triggerGoal(goalId: GoalId, source?: BlockEntity) {
    const map = this.scene.sessionEntity.c.map;
    let count = map.goals[goalId];
    if (count && count > 0) {
      // decrement goal count
      map.goals[goalId] = --count;

      // notify
      this.scene.events.publish({ id: 'goal', goalId, source, count });
    }
  }

  // private: events
  //-------------------------------------------------------------------------
  private _onBlockDespawn(entity: BlockEntity) {
    // By default the goal is the block id
    let goal = entity.c.block.blockId;

    // If the block has a shared goal, use that instead
    if (entity.c.block.props.sharedGoal) {
      goal = entity.c.block.props.sharedGoal;
    }

    this.triggerGoal(goal);
  }

  public getSaveStateData() {
    // Parse current gameplay state into save state data
    const result = getSaveStateData({
      mapDef: this._mapDef!,
      map: this.scene.sessionEntity.c.map,
      level: this.scene.level,
      score: this.scene.scoreSystem.getScore(),
      visualMoves: this.scene.visualMoves,
      moves: this.scene.moves,
      goalCounts: { ...this.scene.sessionEntity.c.map.goals },
      continues: this.scene.continues,
      extraMoves: this.scene.extraMoves,
      socialContinueMoves: this.scene.socialContinueMoves,
      adContinueMoves: this.scene.adContinueMoves,
      usedBoosters: this.scene.usedBoosters,
      blocksBroken: this.scene.blocksBroken,
      powerBlocksCreated: this.scene.powerBlocksCreated,
      blocksBrokenAnalytics: this.scene.blocksBrokenAnalytics,
      scoringConfig: this.scene.scoringConfig,
    });

    // Seems that is possible the exporter might create data that cannot be reimported, like when
    // moves is 0, it encodes just fine but the importer can't decode it. This should never happen, but capture it just in case.
    const reimport = new PuzzleMapImporter(result.mapDef).import();
    if (!reimport) {
      captureGenericError(
        'MapDefEncodeError',
        'PuzzleMapImporter could not import encoded map def',
      );
      return null;
    }

    return result;
  }
}
